'use client';

import type { ZodIssueOptionalMessage } from 'zod';

import type { T } from './utils';

import {
  formatCurrency,
  formatPercent,
} from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { ZodIssueCode, z } from 'zod';

const zodErrorMap = (issue: ZodIssueOptionalMessage, t: T) => {
  if (issue.code === ZodIssueCode.custom) {
    if (issue.params?.type === 'required') {
      return { message: t('common.error.required') };
    }

    if (issue.params?.type === 'invalid_phone_number') {
      return {
        message: t('common.error.invalid-phone-number'),
      };
    }

    if (issue.params?.type === 'invalid_post_code') {
      return {
        message: t('common.error.invalid-post-code'),
      };
    }

    if (issue.params?.type === 'value_too_small') {
      return {
        message: t('common.error.value-too-small', {
          value: issue.params?.minimum,
        }),
      };
    }

    if (issue.params?.type === 'value_too_high') {
      return {
        message: t('common.error.value-too-high', {
          value: issue.params?.maximum,
        }),
      };
    }

    if (issue.params?.type === 'value_length') {
      return {
        message: t('common.error.value-length', {
          value:
            issue.params?.values.length === 1
              ? issue.params?.values[0]
              : `${issue.params?.values.slice(0, -1).join(', ')} ${t('common.or')} ${issue.params?.values.slice(-1)}`,
        }),
      };
    }

    if (issue.params?.type === 'currency_value_too_small') {
      return {
        message: t('common.error.value-too-small', {
          value: formatCurrency(issue.params?.minimum, {
            hasSymbol: true,
            isCompact: false,
          }),
        }),
      };
    }

    if (issue.params?.type === 'currency_value_too_high') {
      return {
        message: t('common.error.value-too-high', {
          value: formatCurrency(issue.params?.maximum, {
            hasSymbol: true,
            isCompact: false,
          }),
        }),
      };
    }

    if (issue.params?.type === 'percent_value_match') {
      return {
        message: t('common.error.value-match', {
          value: formatPercent(issue.params?.value),
        }),
      };
    }

    if (issue.params?.type === 'percent_value_too_small') {
      return {
        message: t('common.error.value-too-small', {
          value: formatPercent(issue.params?.minimum),
        }),
      };
    }

    if (issue.params?.type === 'percent_value_too_high') {
      return {
        message: t('common.error.value-too-high', {
          value: formatPercent(issue.params?.maximum),
        }),
      };
    }

    if (issue.params?.type === 'invalid_date') {
      return {
        message: t('common.error.invalid-date'),
      };
    }

    if (issue.params?.type === 'check_date') {
      return {
        message: t('common.error.check-date'),
      };
    }

    if (issue.params?.type === 'invalid_iban') {
      return {
        message: t('common.error.invalid-iban'),
      };
    }

    if (issue.params?.type === 'invalid_iban_country') {
      return {
        message: t('common.error.invalid-iban-country'),
      };
    }

    if (issue.params?.type === 'invalid_name_characters') {
      return {
        message: t('common.error.invalid-name-characters'),
      };
    }

    return { message: t('common.error.required') };
  }

  if (
    issue.code === ZodIssueCode.invalid_type ||
    issue.code === ZodIssueCode.invalid_literal
  ) {
    return { message: t('common.error.required') };
  }

  if (issue.code === ZodIssueCode.too_small) {
    if (issue.message === 'currency') {
      return {
        message: t('common.error.value-too-small', {
          value: `${issue.minimum}€`,
        }),
      };
    }

    if (issue.type === 'number') {
      return {
        message: t('common.error.value-too-small', {
          value: `${issue.minimum}`,
        }),
      };
    }

    if (issue.type === 'string') {
      return {
        message: t('common.error.minimum-characters', {
          value: Number(issue.minimum),
        }),
      };
    }
    return {
      message: t('common.error.minimum-elements', {
        value: Number(issue.minimum),
      }),
    };
  }

  if (issue.code === ZodIssueCode.too_big) {
    if (issue.type === 'number') {
      return {
        message: t('common.error.value-too-high', {
          value: `${issue.maximum}`,
        }),
      };
    }

    if (issue.type === 'string') {
      return {
        message: t('common.error.maximum-characters', {
          value: Number(issue.maximum),
        }),
      };
    }

    return {
      message: t('common.error.maximum-elements', {
        value: Number(issue.maximum),
      }),
    };
  }

  return { message: t('common.error.required') };
};

export const ZodErrorSetter = () => {
  const t = useTranslations();

  z.setErrorMap((issue) => zodErrorMap(issue, t));

  return null;
};
